import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import useDeviceModeFlg from "./useDeviceModeFlg";
import { MasterProvider } from "./MasterContext";
import { RECENT_SEARCHES_STORAGE_KEY } from "../bop020/navigationState";
import { UNAUTHORIZED_EVENT } from "./apiError";
import "./commonLayout.css";

const MENU_ITEMS = [
  { path: "/bop020w010", label: "収支登録画面" },
  { path: "/bop020w020", label: "収支一覧画面" },
  { path: "/bop020w030", label: "月次集計画面" },
  { path: "/bop020w040", label: "収支推移画面" },
  { path: "/bop030w010", label: "項目管理" },
  { path: "/bop030w020", label: "AI家計相談" },
  { path: "/bop030w030", label: "定期収支管理" },
];

// この量(px)以上スクロールしたら「先頭へ戻る」ボタンを表示する
const SCROLL_TOP_BUTTON_THRESHOLD = 200;

const CommonLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loginUserId, setLoginUserId] = useState("");
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const deviceModeFlg = useDeviceModeFlg();

  const handleLogout = async () => {
    setOpen(false);
    // 共用PCで次の利用者に家計データを見られないよう、認証Cookieと端末に残した検索条件を消す
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error(err);
    }
    try {
      localStorage.removeItem(RECENT_SEARCHES_STORAGE_KEY);
    } catch {
      // ストレージが使えない環境では消す対象もないため無視する
    }
    navigate("/", { replace: true });
  };

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > SCROLL_TOP_BUTTON_THRESHOLD);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // 未ログイン（ログアウト後に「戻る」で来た場合や認証切れ）なら、画面を表示せずログイン画面へ戻す
  useEffect(() => {
    const redirectToLogin = () => navigate("/", { replace: true });

    const fetchLoginUser = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/me`,
          { credentials: "include" },
        );
        if (res.status === 401) {
          redirectToLogin();
          return;
        }
        if (res.ok) {
          const data = await res.json();
          setLoginUserId(data.userId || "");
        }
      } catch (err) {
        console.error(err);
      }
      // 401以外の失敗（通信エラー等）は認証状態が判断できないため画面を表示し、各画面のエラー表示に任せる
      setIsAuthChecked(true);
    };

    // ブラウザのページキャッシュ（bfcache）から復元された場合もログイン状態を確認し直す
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        fetchLoginUser();
      }
    };

    fetchLoginUser();
    window.addEventListener(UNAUTHORIZED_EVENT, redirectToLogin);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, redirectToLogin);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [navigate]);

  if (!isAuthChecked) {
    return null;
  }

  return (
    <div
      className={`commonLayout-container ${
        deviceModeFlg ? "deviceMode-sp" : "deviceMode-pc"
      }`}
    >
      {/* ヘッダー */}
      <header className="commonLayout-header">
        <button
          type="button"
          className="commonLayout-title"
          onClick={() => navigate("/bop020w010")}
        >
          家計簿
        </button>

        <div className="header-right">
          {loginUserId && (
            <span className="login-user">{loginUserId}</span>
          )}
          <button className="logout-button" onClick={handleLogout}>
            ログアウト
          </button>
        </div>
      </header>

      {/* 本体エリア */}
      <div className="commonLayout-body">
        {/* 常に表示される細バー（アイコンだけでなくバー全体のクリックで開閉する） */}
        <div className="side-trigger" onClick={() => setOpen((prev) => !prev)}>
          <button type="button" aria-label="メニューを開閉" aria-expanded={open}>
            ☰
          </button>
        </div>
        {/* サイドバー */}
        <aside className={`sidebar ${open ? "open" : ""}`}>
          {MENU_ITEMS.map((item) => (
            <button
              key={item.path}
              type="button"
              className="menu-item"
              onClick={() => {
                navigate(item.path);
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </aside>

        {/* 画面表示 */}
        <main className="commonLayout-content">
          <MasterProvider>
            <Outlet />
          </MasterProvider>
        </main>
      </div>

      {showScrollTop && (
        <button
          type="button"
          className="scroll-top-button"
          aria-label="ページの先頭へ戻る"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          ▲
        </button>
      )}
    </div>
  );
};

export default CommonLayout;
